// Date captions for the chat set. Components take ISO strings and an optional `now` so the same
// data renders the same text in tests, screenshots and the app (the clock is the caller's).

const DAY_MS = 86_400_000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "14:05" in the viewer's locale. */
export function timeOf(iso: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(
    new Date(iso),
  );
}

/** Full date and time for a `title`/tooltip: "Monday 19 October 2026, 14:05". */
export function fullDateOf(iso: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short" }).format(
    new Date(iso),
  );
}

/** "Today", "Yesterday", or "Monday 12 October" (the year only when it is not this year's). */
export function dayLabel(iso: string, now: Date = new Date(), locale?: string): string {
  const d = new Date(iso);
  const diff = Math.round((startOfDay(now) - startOfDay(d)) / DAY_MS);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  }).format(d);
}

/** True when both instants fall on the same local calendar day. */
export function sameDay(a: string, b: string): boolean {
  return startOfDay(new Date(a)) === startOfDay(new Date(b));
}

/** "now", "5m", "3h", "2d", then a short date: the compact relative time of list rows. */
export function relativeTime(iso: string, now: Date = new Date(), locale?: string): string {
  const d = new Date(iso);
  const s = Math.max(0, Math.round((now.getTime() - d.getTime()) / 1000));
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < DAY_MS / 1000) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * (DAY_MS / 1000)) return `${Math.floor(s / (DAY_MS / 1000))}d`;
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(d);
}
